import * as THREE from "three";
import debug from "debug";

const log = debug("app:arrow");

/**
 * Class for visualizing connection points in a 3D scene by adding arrows to them.
 * @category Experience
 * @subcategory ObjectManagement
 */

class PortDirection {
  /**
   * Initializes the PortDirection with the given experience instance.
   * @param {Object} experience - The experience instance containing the scene and event hub.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = this.experience.eventHub;
    this.isVisualizationEnabled = false; // Property to enable/disable visualization
    this.setEventListeners();
  }

  cleanup() {
    this.eventHub.off("xmd", this.visualizeConnectionPoints);

    this.scene.traverse((object) => {
        if (object.name === "ConnPoint") {
            const arrows = object.children.filter(child => child instanceof THREE.ArrowHelper);
            arrows.forEach(arrow => {
                object.remove(arrow);
                arrow.dispose && arrow.dispose(); // Clean up if dispose method exists
            });
        }
    });

    this.scene = null;
    this.eventHub = null;
    this.isVisualizationEnabled = false;

    log("PortDirection instance cleaned up.");
}

  /**
   * Sets up event listeners for connection point visualization.
   */
  setEventListeners() {
    this.eventHub.on("xmd", this.visualizeConnectionPoints.bind(this));
  }

  /**
   * Visualizes connection points in the scene by adding arrows to them.
   * Skips visualization if it is disabled.
   */
  visualizeConnectionPoints() {
    if (!this.isVisualizationEnabled) {
      log("Visualization is disabled.");
      return;
    }

    try {
      this.scene.traverse((object) => {
        if (object.name === "ConnPoint") {
          log(`Found ConnPoint:`, object);
          this.addArrowToConnPoint(object);
        }
      });
    } catch (error) {
      log("Error during visualization traversal:", error);
    }
  }

  /**
   * Adds an arrow to a connection point to indicate its direction.
   * @param {THREE.Object3D} connPoint - The connection point object to which the arrow is added.
   */
  addArrowToConnPoint(connPoint) {
    try {
      // Get the relative position directly from the ConnPoint's position property

      // Get the direction vector from the ConnPoint group
      const direction = connPoint.direction.normalize(); // Assuming direction is a Vector3

      const length = 1; // Length of the arrow
      const color = 0xff0000; // Red arrow

      // Create an arrow using THREE.ArrowHelper
      const arrowHelper = new THREE.ArrowHelper(
        direction, // Direction vector, directly from the connPoint's direction property
        new THREE.Vector3(0, 0, 0), // Position the arrow at the relative position of the ConnPoint group
        length,
        color
      );

      // Add the arrow to the ConnPoint group
      connPoint.add(arrowHelper);

      log(
        "Arrow added to ConnPoint:",
        connPoint.userData.typeOfPort,
        "of a parent",
        connPoint.parent,
        "at position",
        connPoint.position,
        "with direction",
        direction.toArray()
      );
    } catch (error) {
      log("Error while adding arrow to ConnPoint:", error);
    }
  }
}

export default PortDirection;
